import {registerBrick, Transaction, Query, Predicate, File as OFile, CloudObject} from 'olympe';
import { ReactBrick } from '@olympeio/core';
import { combineLatest } from 'rxjs';
import React from 'react';
import DocumentationArticle from './models/Documentation Article';
import DocumentationTree from './models/Documentation Tree';
import { Box, Paper, Stack, IconButton, Tooltip, TextField, Typography, Divider, Button, Skeleton, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import SchemaIcon from '@mui/icons-material/Schema';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';

export default class DocumentationEditorViewer extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Selected Article', false),
            $.observe('Tree Id', false),
            $.observe('Is Edit', false)
        ]);
    }

    init($) {
        super.init($);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [selArticle, treeId, isEditProp] = props.values;
            const article = selArticle?.toObject(true);
            const [tree, setTree] = React.useState([]);
            const selectedTagId = selArticle?.getTag?.();
            const [title, setTitle] = React.useState('');
            const [file, setFile] = React.useState(null);
            const [content, setContent] = React.useState(article?.content || '');
            const [isEdit, setIsEdit] = React.useState(false);
            const [titleResolved, setTitleResolved] = React.useState(false);
            const [menuEl, setMenuEl] = React.useState(null);
            const menuOpen = Boolean(menuEl);
            const [isDiagramEdit, setIsDiagramEdit] = React.useState(false);
            const [diagramXml, setDiagramXml] = React.useState('');
            const [fileName, setFileName] = React.useState('file.drawio');
            const [treeTag, setTreeTag] = React.useState('');
            const [diagramVersion, setDiagramVersion] = React.useState(0);
            const diagramContainerRef = React.useRef(null);
            const uploadInputRef = React.useRef(null);
            // Template for a blank diagrams.net file
            const EMPTY_DIAGRAM_XML = '<mxfile host="app.diagrams.net" modified="1970-01-01T00:00:00.000Z" agent="Chrome" version="20.8.3" type="device"><diagram id="empty" name="Page-1"><mxGraphModel dx="906" dy="482" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="850" pageHeight="1100" math="0" shadow="0"><root><mxCell id="0"/><mxCell id="1" parent="0"/></root></mxGraphModel></diagram></mxfile>';
            // Convenience: does a diagram exist for this article?
            const hasDiagram = Boolean(file) && Boolean(diagramXml);

            React.useEffect(() => {
                if (!selArticle) {
                    setFile(null);
                    return;
                }

                const fetchFile = async () => {
                    try {
                        const query = await Query.from(selArticle.getTag())
                            .follow(DocumentationArticle.drawioDiagramRel)
                            .andReturn()
                            .execute();
                        const foundFile = query.getFirst?.();
                        if (foundFile) {
                            setFile(foundFile);
                        } else {
                            setFile(null);
                        }
                    } catch (e) {
                        console.error('Error fetching related file:', e);
                        setFile(null);
                    }
                };
                fetchFile();
            }, [selArticle]);

            // Read OFile and convert to XML for diagrams.net viewer
            React.useEffect(() => {
                if (!file) {
                    setDiagramXml('');
                    return;
                }
                const fileObj = file.toObject?.(true);
                file.getContentAsString()
                    .then((content) => {
                        let xml;
                        try {
                            const decoded = atob(content);
                            if (decoded.startsWith('<mxfile')) {
                                xml = decoded;
                            } else {
                                throw new Error('Not base64 XML');
                            }
                        } catch {
                            xml = content;
                        }
                        setFileName(fileObj?.['file name'] || 'file.drawio');
                        setDiagramXml(xml || '');
                    })
                    .catch((error) => {
                        console.error('Failed to load diagram XML:', error);
                        setDiagramXml('');
                    });
            }, [file]);

            // Load diagrams.net viewer script once
            React.useEffect(() => {
                const script = document.createElement('script');
                script.src = 'https://viewer.diagrams.net/js/viewer.min.js';
                script.async = true;
                document.body.appendChild(script);
                return () => {
                    document.body.removeChild(script);
                };
            }, []);

            // Bump diagram version so the viewer DOM remounts on new XML (avoids sizing glitches)
            React.useEffect(() => {
                if (!isDiagramEdit && diagramXml) {
                    setDiagramVersion((v) => v + 1);
                }
            }, [diagramXml, isDiagramEdit]);

            // Process elements when diagram XML is ready (only in view mode)
            React.useEffect(() => {
                if (!diagramXml || isDiagramEdit) return;
                let raf1 = 0, raf2 = 0, timer = 0;

                const run = () => {
                    try {
                        if (diagramContainerRef.current) {
                            const payload = {
                                highlight: '#0000ff',
                                nav: true,
                                resize: true,
                                xml: diagramXml,
                            };
                            diagramContainerRef.current.setAttribute('data-mxgraph', JSON.stringify(payload));
                        }
                        window.GraphViewer?.processElements();
                    } catch (e) {
                        console.warn('GraphViewer not ready yet:', e);
                    }
                };

                raf1 = window.requestAnimationFrame(() => {
                    raf2 = window.requestAnimationFrame(() => {
                        timer = window.setTimeout(run, 30);
                    });
                });

                return () => {
                    if (raf1) cancelAnimationFrame(raf1);
                    if (raf2) cancelAnimationFrame(raf2);
                    if (timer) clearTimeout(timer);
                };
            }, [diagramXml, isDiagramEdit, diagramVersion]);

            function findLabelByTagId(nodes, targetId) {
                if (!Array.isArray(nodes) || !targetId) return null;
                for (const node of nodes) {
                    if (node?.tag_id === targetId) return node?.label || null;
                    const fromChildren = findLabelByTagId(node?.children || [], targetId);
                    if (fromChildren) return fromChildren;
                }
                return null;
            }

            React.useEffect(() => {
                if (!treeId) return;
                setTitleResolved(false);
                const fetchTree = async () => {
                    try {
                        const query = await Query.instancesOf(DocumentationTree)
                            .filter(Predicate.equals(DocumentationTree.idProp, treeId))
                            .execute();
                        const raw = query.getFirst()?.getMUIJSONData?.();
                        setTreeTag(query.getFirst().getTag() ?? '');

                        let parsed = [];
                        if (Array.isArray(raw)) {
                            parsed = raw;
                        } else if (typeof raw === 'string') {
                            try {
                                parsed = JSON.parse(raw) || [];
                            } catch (e) {
                                console.error('Failed to parse tree JSON:', e, raw);
                                parsed = [];
                            }
                        } else if (raw && typeof raw === 'object') {
                            parsed = raw;
                        }
                        setTree(parsed);
                    } catch (err) {
                        console.error('Error fetching tree:', err);
                        setTree([]);
                    }
                };
                fetchTree();
            }, [treeId, selArticle]);


            React.useEffect(() => {
                setContent(article?.content || '');
            }, [article?.content, selArticle]);

            React.useEffect(() => {
                const lbl = findLabelByTagId(tree, selectedTagId);
                if (!isEdit) {
                    setTitle(lbl || '');
                }
                // Mark as resolved once we've attempted to compute the label
                setTitleResolved(true);
            }, [tree, selectedTagId, isEdit]);

            function updateLabelInTree(nodes, targetId, newLabel) {
                if (!Array.isArray(nodes) || !targetId) return nodes;
                let changed = false;
                const next = nodes.map((node) => {
                    if (!node) return node;
                    if (node.tag_id === targetId) {
                        changed = true;
                        return {...node, label: newLabel};
                    }
                    const children = Array.isArray(node.children) ? node.children : [];
                    const updatedChildren = updateLabelInTree(children, targetId, newLabel);
                    if (updatedChildren !== children) {
                        changed = true;
                        return {...node, children: updatedChildren};
                    }
                    return node;
                });
                return changed ? next : nodes;
            }

            const handleSave = React.useCallback(() => {
                const t = new Transaction(true);
                const tag = selArticle.getTag();
                DocumentationArticle.setContent(t, tag, content);
                DocumentationTree.setMUIJSONData(t, treeTag, JSON.stringify(updateLabelInTree(tree, tag, title)));
                t.execute().then(() => {
                    setTree((prev) => updateLabelInTree(prev, tag, title));
                    setIsEdit(false);
                    $.trigger('On Title Change');
                }).catch((e) => {
                    console.error('Unable to save:', e);
                });
            }, [content, selArticle, title, treeId]);

            const handleEditToggle = React.useCallback(() => {
                setIsEdit((prev) => !prev);
            }, []);

            const handleMenuOpen = React.useCallback((e) => setMenuEl(e.currentTarget), []);
            const handleMenuClose = React.useCallback(() => setMenuEl(null), []);
            const handleMenuEdit = React.useCallback(() => {
                handleMenuClose();
                handleEditToggle();
            }, [handleEditToggle, handleMenuClose]);

            const handleUploadDiagram = React.useCallback(() => {
                handleMenuClose();
                // Open native file picker
                uploadInputRef.current?.click();
            }, [handleMenuClose]);

            const handleDownloadDiagram = React.useCallback(() => {
                handleMenuClose();
                if (!diagramXml) return;
                try {
                    const blob = new Blob([diagramXml], { type: 'text/xml' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName || 'diagram.drawio';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                } catch (e) {
                    console.error('Failed to download diagram:', e);
                }
            }, [handleMenuClose, diagramXml, fileName]);

            const handleEditDiagram = React.useCallback(() => {
                handleMenuClose();
                if (!hasDiagram) {
                    // Seed a new, empty diagram so the editor opens with valid XML
                    setDiagramXml(EMPTY_DIAGRAM_XML);
                    setFileName('diagram.drawio');
                }
                setIsDiagramEdit(true);
            }, [handleMenuClose, hasDiagram]);

            React.useEffect(() => {
                if (!isDiagramEdit) return;

                const handleMessage = (event) => {
                    if (event?.data === 'ready') {
                        const iframe = document.getElementById('embed-diagram');
                        if (iframe && iframe.contentWindow) {
                            iframe.contentWindow.postMessage(diagramXml || '', '*');
                        }
                    } else if (typeof event?.data === 'string' && event.data.startsWith('<mxfile')) {
                        const transaction = new Transaction(true);
                        let created = null;
                        if (file) {
                            // Update existing file
                            OFile.setContent(transaction, file.getTag(), fileName || 'diagram.drawio', event.data, 'text/xml');
                        } else {
                            // Create new file
                            created = transaction.create(OFile);
                            OFile.setContent(transaction, created, fileName || 'diagram.drawio', event.data, 'text/xml');
                            transaction.createRelation(DocumentationArticle.drawioDiagramRel, selArticle, created);
                        }
                        transaction.execute()
                            .then(() => {
                                try {
                                    if (file) {
                                        // Refresh current file
                                        setFile(CloudObject.get(file.getTag()));
                                    } else if (created) {
                                        // Set the newly created file so future actions (download, etc.) work
                                        setFile(CloudObject.get(created));
                                    }
                                    // Exit edit mode first so the viewer div mounts, then set XML to trigger processing
                                    setIsDiagramEdit(false);
                                    setDiagramXml(event.data);
                                } catch (e) {
                                    console.warn('Could not set CloudObject for file, updating XML only:', e);
                                    setIsDiagramEdit(false);
                                    setDiagramXml(event.data);
                                }
                            })
                            .catch((message) => {
                                console.error('Failed to save diagram file:', message);
                            });
                    }
                };

                window.addEventListener('message', handleMessage);
                return () => window.removeEventListener('message', handleMessage);
            }, [isDiagramEdit, diagramXml, fileName, file]);

            const quillModules = React.useMemo(() => ({
                toolbar: [
                    [{header: [1, 2, 3, false]}],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{script: 'sub'}, {script: 'super'}],
                    [{list: 'ordered'}, {list: 'bullet'}],
                    [{align: []}],
                    ['blockquote', 'code-block'],
                    ['link', 'image'],
                    ['clean']
                ]
            }), []);

            // Empty state
            if (!selArticle || !article) {
                return (
                    <Paper variant="outlined" sx={{
                        height: 'calc(100% - 2px)',
                        maxHeight: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Typography color="text.secondary">Select a page on the left to view or edit
                            documentation.</Typography>
                    </Paper>
                );
            }

            return (
                <Paper variant="outlined" elevation={0} sx={{
                    height: 'calc(100% - 2px)',
                    maxHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative'
                }}>
                    {/* Header row: title (textfield in edit) + actions */}
                    <Box sx={{px: 2, pt: 1.25, pb: 1}}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {isEdit ? (
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Title"
                                />
                            ) : (
                                titleResolved ? (
                                    <Typography variant="h6" sx={{flex: 1, pr: 2}}>{title || ''}</Typography>
                                ) : (
                                    <Box sx={{flex: 1, pr: 2}}>
                                        <Skeleton variant="text" width={180} />
                                    </Box>
                                )
                            )}
                            {isEdit ? (
                                <Tooltip title="Save">
                                    <span>
                                        <IconButton color="primary" onClick={handleSave} size="small">
                                            <SaveIcon/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Actions">
                                    <span>
                                        <IconButton onClick={handleMenuOpen} size="small">
                                            <EditIcon/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Stack>
                        <Menu
                            anchorEl={menuEl}
                            open={menuOpen}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { sx: { minWidth: 220 } } }}
                        >
                            <MenuItem onClick={handleMenuEdit}>
                                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Edit Documentation" />
                            </MenuItem>
                            <MenuItem onClick={handleEditDiagram}>
                                <ListItemIcon><SchemaIcon fontSize="small" /></ListItemIcon>
                                <ListItemText primary={hasDiagram ? 'Edit Diagram' : 'Create Diagram'} />
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={handleUploadDiagram}>
                                <ListItemIcon><CloudUploadIcon fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Upload Diagram" />
                            </MenuItem>
                            <MenuItem onClick={handleDownloadDiagram} disabled={!hasDiagram}>
                                <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Download Diagram" />
                            </MenuItem>
                        </Menu>
                        {/* Hidden file input for upload */}
                        <input
                            ref={uploadInputRef}
                            type="file"
                            accept=".drawio,.xml,text/xml,application/xml"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const f = e.target.files && e.target.files[0];
                                if (!f) return;
                                const reader = new FileReader();
                                reader.onload = () => {
                                    const text = typeof reader.result === 'string' ? reader.result : '';
                                    // Update local UI immediately
                                    setFileName(f.name || 'diagram.drawio');
                                    setDiagramXml(text || '');

                                    // Persist to Olympe: create or update OFile and (if needed) link to article
                                    const t = new Transaction(true);
                                    let created = null;
                                    if (file) {
                                        OFile.setContent(t, file.getTag(), f.name || 'diagram.drawio', text, 'text/xml');
                                    } else {
                                        created = t.create(OFile);
                                        OFile.setContent(t, created, f.name || 'diagram.drawio', text, 'text/xml');
                                        // Link the uploaded file to the selected article
                                        t.createRelation(DocumentationArticle.drawioDiagramRel, selArticle, created);
                                    }
                                    t.execute()
                                        .then(() => {
                                            try {
                                                if (file) {
                                                    setFile(CloudObject.get(file.getTag()));
                                                } else if (created) {
                                                    setFile(CloudObject.get(created));
                                                }
                                            } catch (e) {
                                                console.warn('Could not set CloudObject for uploaded file:', e);
                                            }
                                        })
                                        .catch((err) => console.error('Upload persist failed:', err))
                                        .finally(() => {
                                            // reset the input so selecting the same file again triggers onChange
                                            e.target.value = '';
                                        });
                                };
                                reader.onerror = (err) => {
                                    console.error('Failed to read uploaded file:', err);
                                    e.target.value = '';
                                };
                                reader.readAsText(f);
                            }}
                        />
                        <Divider sx={{mt: 1}}/>
                    </Box>

                    {/* Content area */}
                    <Box sx={{flex: 1, px: 2, py: 1.25, display: 'block', overflowY: 'auto', minHeight: 320}}>
                        <Box
                            sx={{
                                '& .ql-container': {border: 'none', borderWidth: 0},
                                '& .ql-editor': {overflow: 'auto'},
                            }}
                        >
                            {isEdit ? (
                                <ReactQuill
                                    key="edit-mode"
                                    theme="snow"
                                    value={content || ''}
                                    onChange={setContent}
                                    modules={quillModules}
                                    style={{width: '100%', minHeight: 320}}
                                />
                            ) : (
                                <Box sx={{
                                    '& .ql-container.ql-snow': { border: 'none' },
                                    '& .ql-editor': { padding: 0, fontFamily: 'inherit' }
                                }}>
                                    <div className="ql-container ql-snow">
                                        <div className="ql-editor" dangerouslySetInnerHTML={{ __html: content || '' }} />
                                    </div>
                                </Box>
                            )}
                        </Box>
                        {diagramXml && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Diagram</Typography>
                                <div
                                    key={diagramVersion}
                                    ref={diagramContainerRef}
                                    className="mxgraph"
                                    data-mxgraph={JSON.stringify({
                                        highlight: '#0000ff',
                                        nav: true,
                                        resize: true,
                                        xml: diagramXml,
                                    })}
                                    style={{ width: '100%', minHeight: 420 }}
                                />
                            </>
                        )}
                        {/* Diagram editor dialog */}
                        <Dialog open={isDiagramEdit} onClose={() => setIsDiagramEdit(false)} fullWidth maxWidth="xl">
                            <DialogTitle>Edit Diagram</DialogTitle>
                            <DialogContent dividers sx={{ p: 0 }}>
                                <Box sx={{ width: '100%', height: '80vh' }}>
                                    <iframe
                                        id="embed-diagram"
                                        src="https://embed.diagrams.net/?embed=1&ui=atlas&spin=1&saveAndExit=1&noSaveBtn=0&noExitBtn=0&nav=1&toolbar=0&edit=1"
                                        width="100%"
                                        height="100%"
                                        title="Draw.io Editor"
                                        style={{ border: 0 }}
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setIsDiagramEdit(false)}>Close</Button>
                            </DialogActions>
                        </Dialog>
                    </Box>
                </Paper>
            );
        };
    }
}

registerBrick('019a070e18e6dc7861a2', DocumentationEditorViewer);
